import { useRef } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { ArrayField, FormApi } from 'informed';
import { addDays, format } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import api from 'api';
import { breakpoints } from 'styles';
import { CalendarEventDateString } from 'components/calendar';
import {
  Alert,
  Button,
  ModalButtonWithForm,
  TargetSchedule
} from 'components/common';
import { Icon } from 'components/common/icons';
import { Checkbox, FormGroup, Select } from 'components/common/form';
import { PathStepsAddOrEditWarning } from '.';

import type {
  GetCalendarEventsMinimalRes,
  GetPathStepsMinimalRes
} from 'shared-types/responses';
import type { Achievement, Course } from 'shared-types';

interface Props {
  availableAchievements: Achievement[];
  availableCourses: Course[];
  availableEvents: GetCalendarEventsMinimalRes['events'];
  existingAchievements?: number[];
  existingCourses?: number[];
  existingEvents?: number[];
  step?: GetPathStepsMinimalRes['steps'][0];
  updateSteps: () => Promise<void>;
}

const PathStepsAddOrEdit = ({
  availableAchievements,
  availableCourses,
  availableEvents,
  existingAchievements,
  existingCourses,
  existingEvents,
  step,
  updateSteps
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path },
    serverConfig: {
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      timeZone
    },
    theme
  } = useReduxState();

  const formApi = useRef<FormApi<typeof initialValues>>(undefined);

  const achievementsVisible = !hideAchievementsFromUI;
  const calendarEventsVisible = !hideCalendarEventsFromUI;
  const coursesVisible = !hideCoursesFromUI;

  const handleSubmit = async (values: typeof initialValues) => {
    const restMethod = step ? 'put' : 'post';
    const url = step
      ? `/admin/paths/${path.id}/steps/${step.id}`
      : `/admin/paths/${path.id}/steps`;

    try {
      const response = await api[restMethod](url, {
        ...values,
        ...(step
          ? {
              isMandatory: cannotSetAsMandatory
                ? !cannotSetAsMandatory
                : values.isMandatory !== undefined
                ? values.isMandatory
                : true
            }
          : {}),
        deadlineDate: values.deadlineDate
          ? toDate(`${format(values.deadlineDate, 'y-MM-dd')}T23:59:59`, {
              timeZone
            })
          : null
      });
      if (!response.data && !response.data.success) return false;

      await updateSteps();

      dispatch.addNotice({
        text: step
          ? messages.pathsStepsEditStepSuccess
          : messages.pathsStepsAddStepSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateRequired = (value) => {
    return value ? undefined : messages.commonFieldRequired;
  };

  const achievementOptions = availableAchievements.map((achievement) => ({
    label: achievement.title,
    value: achievement.id
  }));

  const courseOptions = availableCourses.map((course) => ({
    label: course.title,
    value: course.id
  }));

  const eventOptions = availableEvents.map((event) => ({
    helpText: (
      <div
        style={{
          color:
            new Date(event.endDate) >= new Date()
              ? theme.textColorLighter
              : theme.dangerColor
        }}
      >
        <CalendarEventDateString
          startDate={event.startDate as string}
          endDate={event.endDate as string}
        />
      </div>
    ),
    label: event.title,
    value: event.id
  }));

  // add existing items as options if they are not there yet, this may occur
  // ex. if another admin has added an item that the current admin does
  // not have access to
  if (step?.items?.length) {
    step.items.forEach((item) => {
      if (
        item.type === 'achievement' &&
        !achievementOptions.find(
          (option) => option.value === item.achievement.id
        )
      ) {
        achievementOptions.push({
          label: item.achievement.title,
          value: item.achievement.id
        });
      } else if (
        item.type === 'course' &&
        !courseOptions.find((option) => option.value === item.course.id)
      ) {
        courseOptions.push({
          label: item.course.title,
          value: item.course.id
        });
      } else if (
        item.type === 'event' &&
        !eventOptions.find((option) => option.value === item.event.id)
      ) {
        eventOptions.push({
          helpText: (
            <div
              style={{
                color:
                  new Date(item.event.endDate) >= new Date()
                    ? theme.textColorLighter
                    : theme.dangerColor
              }}
            >
              <CalendarEventDateString
                startDate={item.event.startDate as string}
                endDate={item.event.endDate as string}
              />
            </div>
          ),
          label: item.event.title,
          value: item.event.id
        });
      }
    });

    const sortByLabel = (a, b) => (a.label > b.label ? 1 : -1);

    achievementOptions.sort(sortByLabel);
    courseOptions.sort(sortByLabel);
    eventOptions.sort(sortByLabel);
  }

  const contentOptions = {
    achievement: achievementOptions,
    course: courseOptions,
    event: eventOptions
  };

  const uncompletableCourses =
    step?.items.filter(
      (item) =>
        item.type === 'course' &&
        (!item.course.completionEnabled || item.course.userAccess === 'guests')
    ) || [];

  const cannotSetAsMandatory =
    uncompletableCourses.length === step?.items.length;

  const defaultType = coursesVisible
    ? 'course'
    : achievementsVisible
    ? 'achievement'
    : 'event';

  const initialValues = {
    items: [
      ...(step?.items?.map((item) => ({
        id: item.achievement?.id || item.course?.id || item.event?.id,
        title:
          item.achievement?.title || item.course?.title || item.event?.title,
        type: item.type
      })) || [{ id: null, title: '', type: defaultType }])
    ],
    deadlineDate: step?.deadlineDate
      ? Date.parse(toZonedTime(step?.deadlineDate, timeZone).toString())
      : Date.parse(addDays(dateNow, 1).toString()),
    deadlineDurationUnit: step?.deadlineDurationUnit || 'days',
    deadlineDurationValue: step?.deadlineDurationValue || 7,
    deadlineType: step?.deadlineType || 'none',
    deadlineDenyAccess: step?.deadlineDenyAccess,
    isMandatory: step?.isMandatory
  };

  return (
    <ModalButtonWithForm
      initialValues={initialValues}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<typeof initialValues>) =>
        (formApi.current = form)
      }
      {...(step
        ? {
            buttonCategory: 'lighter',
            buttonIcon: 'cog',
            buttonProps: {
              'aria-label': messages.commonSettings,
              'data-tooltip': messages.commonSettings
            },
            buttonSize: 'small',
            buttonStyle: {
              margin: '-2px 0',
              padding: '6px 0',
              width: 30,
              height: 30
            },
            modalActionCategory: 'success',
            modalActionText: messages.commonSave,
            modalTitle: messages.pathsStepsEditSettings
          }
        : {
            buttonCategory: 'success',
            buttonIcon: 'plus',
            buttonSize: 'header',
            buttonText: messages.pathsStepsAddStep,
            modalActionText: messages.commonAdd,
            modalTitle: messages.pathsStepsAddStep
          })}
    >
      {({ values }) => (
        <div className="content">
          {values.items?.length &&
          values.items?.filter((item) => item.type === 'event').length ===
            values.items?.length ? (
            <Alert
              style={{ marginBottom: 10 }}
              text={messages.pathsStepsAlternativesEventOnly}
              type="warning-light"
            />
          ) : null}

          <ArrayField field="items">
            {({ addWithInitialValue }) => (
              <>
                <ArrayField.Items<{ id: number; type: string }>>
                  {({
                    // @ts-ignore
                    errors,
                    field,
                    index,
                    remove,
                    setValue,
                    values: fieldValues
                  }) => (
                    <div className="option-type">
                      <Select
                        id={`${index}-type`}
                        field={`${field}.type`}
                        options={[
                          ...(coursesVisible
                            ? [
                                {
                                  label: messages.commonCourse,
                                  value: 'course'
                                }
                              ]
                            : []),
                          ...(achievementsVisible
                            ? [
                                {
                                  label: messages.achievement,
                                  value: 'achievement'
                                }
                              ]
                            : []),
                          ...(calendarEventsVisible
                            ? [
                                {
                                  label: messages.eventCalendarEvent,
                                  value: 'event'
                                }
                              ]
                            : [])
                        ]}
                        onChange={() => setValue('id', null)}
                        style={{ flexShrink: 0, width: 180 }}
                      />

                      <div className="option-actions">
                        <Select
                          id={`${index}-id`}
                          error={errors?.id}
                          field={`${field}.id`}
                          isSearchable={true}
                          options={contentOptions[fieldValues?.type as string]}
                          value={
                            contentOptions[fieldValues?.type as string]?.find(
                              (option) => option.value === fieldValues?.id
                            ) || []
                          }
                          validate={validateRequired}
                          validateOnChange={true}
                        />

                        {(fieldValues?.type === 'course' &&
                          existingCourses.includes(
                            fieldValues?.id as number
                          )) ||
                        (fieldValues?.type === 'achievement' &&
                          existingAchievements.includes(
                            fieldValues?.id as number
                          )) ||
                        (fieldValues?.type === 'event' &&
                          existingEvents.includes(
                            fieldValues?.id as number
                          )) ? (
                          <div
                            className="warning"
                            data-tooltip={
                              messages.pathsStepsEditSettingsWarning
                            }
                          >
                            <Icon icon="exclamation-triangle" />
                          </div>
                        ) : null}
                      </div>

                      <Button
                        category="light"
                        disabled={values?.items?.length <= 1}
                        icon="trash-alt"
                        onClick={remove}
                        style={{ flexShrink: 0 }}
                        tooltip={messages.commonRemove}
                      />

                      <style jsx>{`
                        .option-type {
                          display: flex;
                          flex-wrap: wrap;
                          gap: 5px;
                          margin-top: 5px;
                        }
                        .option-actions {
                          flex-grow: 1;
                          min-width: 200px;
                          position: relative;
                        }
                        .warning {
                          color: ${theme.warningStandaloneColor};
                          font-size: 1.2rem;
                          position: absolute;
                          top: 10px;
                          right: 28px;
                        }
                        @media (min-width: ${breakpoints.small}px) {
                          .option-type {
                            flex-wrap: nowrap;
                          }
                        }
                      `}</style>
                    </div>
                  )}
                </ArrayField.Items>

                <Button
                  category="light"
                  icon="plus"
                  onClick={() => addWithInitialValue({ type: 'course' })}
                  text={messages.pathsAddAlternative}
                  size="small"
                  style={{ marginTop: 10 }}
                />
              </>
            )}
          </ArrayField>

          {step ? (
            <>
              <FormGroup
                label={messages.commonMandatory}
                style={{ marginTop: 25 }}
              >
                <div
                  {...(cannotSetAsMandatory && !values?.isMandatory
                    ? { 'data-tooltip': messages.pathsStepsMandatoryDisabled }
                    : {})}
                >
                  <Checkbox
                    disabled={cannotSetAsMandatory && !values?.isMandatory}
                    field="isMandatory"
                    id={`is-mandatory-${step?.id || 0}`}
                    label={messages.pathsStepsSetAsMandatory}
                  />
                </div>
              </FormGroup>

              <TargetSchedule id={`${step?.id || 0}`} />

              <Checkbox
                field="deadlineDenyAccess"
                id={`deadline-deny-access-${step?.id || 0}`}
                label={messages.courseTargetScheduleDeny}
              />
            </>
          ) : null}

          <PathStepsAddOrEditWarning
            isEditing={Boolean(step)}
            initialSettings={{
              deadlineDate: initialValues.deadlineDate,
              deadlineDurationUnit: initialValues.deadlineDurationUnit,
              deadlineDurationValue: initialValues.deadlineDurationValue,
              deadlineType: initialValues.deadlineType
            }}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default PathStepsAddOrEdit;
